import { z } from 'zod';
import { emptyAsUndefined, id, moneyAmount, requiredId, ymd } from './common';

/**
 * Import of past daily data from a spreadsheet (Google Sheets / Excel export):
 * one app, optionally one ads account (spend) and one revenue account
 * (revenue) per import. The browser maps the columns; the server validates,
 * previews and saves everything in one transaction.
 */
export const importRowSchema = z.object({
  /** Row number in the sheet (for messages). */
  line: z.number().int().min(1),
  date: ymd,
  /** Ads spend before GST (INR / account currency). Empty = no marketing entry. */
  spend: emptyAsUndefined(moneyAmount),
  /** GST as written in the sheet (used when gstMode = SHEET). */
  gst: emptyAsUndefined(moneyAmount),
  /** Revenue in the revenue account's currency (e.g. USD). Empty = no revenue entry. */
  revenue: emptyAsUndefined(moneyAmount),
  /** Revenue in INR as written in the sheet (used to derive the rate when rateMode = SHEET). */
  revenueInr: emptyAsUndefined(moneyAmount),
});
export type ImportRow = z.output<typeof importRowSchema>;

export const IMPORT_GST_MODES = ['SHEET', 'CALCULATE'] as const;
export const IMPORT_RATE_MODES = ['SHEET', 'MONTHLY'] as const;
export const IMPORT_DUPLICATE_MODES = ['SKIP', 'OVERWRITE'] as const;

export const dailyImportSchema = z
  .object({
    appId: requiredId('App'),
    advertisingAccountId: emptyAsUndefined(id),
    revenueAccountId: emptyAsUndefined(id),
    rows: z
      .array(importRowSchema)
      .min(1, 'No rows to import')
      .max(3000, 'At most 3,000 rows per import'),
    options: z.object({
      /** SHEET: keep the sheet's GST (flagged as invoice GST when it differs); CALCULATE: default rate. */
      gstMode: z.enum(IMPORT_GST_MODES).default('SHEET'),
      /** SHEET: rate = INR ÷ revenue per row (saved as a manual rate); MONTHLY: Currency Rates. */
      rateMode: z.enum(IMPORT_RATE_MODES).default('SHEET'),
      duplicates: z.enum(IMPORT_DUPLICATE_MODES).default('SKIP'),
      /** Start the app ⇄ account assignment on the first imported date when needed. */
      extendAssignments: z.boolean().default(true),
    }),
  })
  .refine((v) => v.advertisingAccountId || v.revenueAccountId, {
    path: ['advertisingAccountId'],
    message: 'Choose an ads account, a revenue account or both',
  });
export type DailyImportInput = z.output<typeof dailyImportSchema>;
